import { reactive, toRaw } from "vue"
import { api, ApiError, apiUrl, configureApi, isTemporary, setToken, uploadFile, wsUrl } from "../lib/api"
import { t } from "../lib/i18n"
import { organisationLogo, shopPicture } from "../lib/pictures"
import type { Severity, Status } from "../lib/incidents"
import { type Attachment, LocalDb, type LocalMessage, type OutboxEntry } from "../lib/localdb"
import { uuid } from "../lib/uuid"

/**
 * Messaging: a person's conversations, kept on the device (IndexedDB), synced with Johku Hub,
 * sent through an outbox that survives a dropped network or a closed app, and hinted in real time.
 * Hub's app runs one session per open account; the POS runs one for the shop's inbox with a POS
 * token, which reaches only the shop's conversations.
 */

export interface Organisation {
  id: string
  name: string
  kind: "johku" | "shop"
  role: string
  /** The shop's picture in its Johku files (lib/pictures.ts), or "" */
  logo?: string
  /** Shops: found by other merchants, and what they read about it */
  listed?: boolean
  about?: string
  /** False: the person turned off notifications of its new messages (an older Hub doesn't say: on) */
  notifyMessages?: boolean
}

export interface Me {
  person: { id: string; email: string; name: string }
  organisations: Organisation[]
}

/** Hub's copy of a Johku order, on order threads. */
export interface OrderContext {
  orderId: string
  orderDate: number | null
  customerName: string
  locale: string
  total: string | null
  /** As Johku has it now, once Johku watches the order */
  status?: "paid" | "confirmed" | "refunded" | "cancelled" | "unpaid"
  /** image: the product's picture in the shop's Johku files (lib/pictures.ts) */
  items: { name: string; count: number; start: string | null; end: string | null; image?: string | null }[]
}

export interface Thread {
  id: string
  /** group: a shop's message to a collection's members; member: one member's conversation with the shop */
  kind: "organisations" | "private" | "order" | "group" | "member"
  subject: string
  lastSeq: number
  lastReadSeq: number
  lastAuthorPersonId: string | null
  /** The latest message is the order's customer's. */
  lastAuthorCustomer?: boolean
  /** You're the customer of this order (it's your own order with a shop). */
  asCustomer?: boolean
  organisations: { id: string; name: string; logo?: string }[]
  people: { id: string; name: string; email: string }[]
  orderContext?: OrderContext | null
  memberContext?: { email: string; name: string; collection: string } | null
  groupContext?: { collection: string; recipients: number; sent: number } | null
  /** Between merchants: the shop that hasn't accepted the conversation yet */
  invitedOrganisationId?: string | null
  declined?: boolean
}

/** A sales channel offer in a merchant conversation (a message of kind "proposal"). */
export interface Proposal {
  messageId: string
  fromOrganisationId: string
  toOrganisationId: string
  channelTitle: string
  commission: number
  status: "pending" | "accepted" | "declined" | "withdrawn"
  appliedAt: string | null
  applyError: string | null
}

/** Something wrong in Johku, as the Johku team tells it (src/incidents.ts). */
export interface Incident {
  id: string
  title: string
  severity: Severity
  status: Status
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  /** The Johku team: the shops it's for, null for every shop */
  shops?: { id: string; name: string }[] | null
  /** Newest first */
  updates: {
    id: string
    status: Status
    body: string
    authorName: string
    createdAt: string
    /** The Johku team: how far sending is */
    progress?: { recipients: number; delivered: number; pushed: number; emailed: number }
  }[]
}

/** A message as the UI shows it: confirmed by sync, or still in the outbox. */
export interface ShownMessage extends Omit<LocalMessage, "seq"> {
  seq: number | null
  status: "delivered" | "sending" | "failed"
  error?: string
}

export interface MessagingOptions {
  /** Johku Hub's address; empty for the same origin. */
  apiBase: string
  /** The person's bearer: an account's session token, or a POS token for one shop. */
  token: string
  /** A POS token's organisation: the session sees only its conversations, and keeps a copy of its own on the device. */
  scope?: string | null
  /** A shared device, like a till: only the person signed in now keeps a copy on it. */
  sharedDevice?: boolean
  /** The server refused the token (401): a new one, or null when there's none. */
  renewToken?: () => Promise<string | null>
  /** The session ended: the token was refused and not renewed ("expired"), or access ended ("no_access"). */
  onEnded?: (reason: "expired" | "no_access") => void
}

interface SyncResult {
  messages: LocalMessage[]
  cursor: number
  hasMore: boolean
  accessVersion: number
}

const POLL_MS = 60_000
/**
 * Up to this many threads, every sync fetches the whole list, as Hub always has (a shop or a few).
 * Someone in many shops gets only the threads that changed, and the whole list now and then.
 */
const FULL_LIST_MAX = 200
const FULL_LIST_MS = 5 * 60_000
/** Changed threads fetched by id at a time; more than this, and the whole list it is */
const CHANGED_MAX = 100
const SHARED_DEVICE_KEY = "hub.sharedDevice.people"

const state = reactive({
  status: "off" as "off" | "ready",
  me: null as Me | null,
  threads: [] as Thread[],
  /** Confirmed messages by thread, oldest first. */
  messages: {} as Record<string, LocalMessage[]>,
  outbox: [] as OutboxEntry[],
  /** False while the server can't be reached; the outbox keeps everything until it can. */
  online: true,
  openThreadId: null as string | null,
  /** Show only one organisation's conversations; null shows everything. */
  orgFilter: null as string | null,
  /** A POS token's organisation: no private conversations then. */
  scope: null as string | null,
  /** A search hit to show when its conversation opens */
  focusMessageId: null as string | null,
  /** Incidents in Johku that concern the person's shops (the Johku team: all of the last weeks) */
  incidents: [] as Incident[],
  /** The person is on the Johku team: posts and updates incidents */
  incidentTeam: false,
  /** The incident on screen ("new": the team writes one, "list": the team's list), or null */
  openIncidentId: null as string | null,
})

let options: MessagingOptions | null = null
let db: LocalDb | null = null
let cursor = 0
let accessVersion: number | undefined
let syncing: Promise<void> | null = null
let syncAgain = false
let retryDelay = 0
let retryTimer: ReturnType<typeof setTimeout> | undefined
let ws: WebSocket | null = null
let wsAttempts = 0
let wsTimer: ReturnType<typeof setTimeout> | undefined
let pollTimer: ReturnType<typeof setInterval> | undefined
let listenersInstalled = false
/** Threads changed without a message the sync brought (realtime "thread" hints, own actions) */
const changedThreads = new Set<string>()
/** When the whole thread list came last; 0: needed at the next sync */
let fullListAt = 0
let renewing: Promise<boolean> | null = null
let lastRenewal = 0

const plain = <T extends object>(value: T): T => ({ ...toRaw(value) })

export function useMessaging() {
  return state
}

/** The device copy's name: one per person, and one per person and shop for a POS token. */
export function localDbKey(personId: string, scope?: string | null): string {
  return scope ? `${personId}.${scope}` : personId
}

/** Who the token belongs to, and their organisations (a POS token: only its shop). */
export async function fetchMe(apiBase: string, token: string): Promise<Me> {
  configureApi(apiBase)
  return (await api<Me>("/api/me", { token })).data
}

// ---- the session ------------------------------------------------------------

/**
 * Opens a person's conversations from the device copy, then syncs, connects realtime and polls as a
 * safety net. `offline`: the caller couldn't reach Hub and started from what the device has.
 */
export async function startMessaging(opts: MessagingOptions, me: Me, offline = false): Promise<void> {
  stopMessaging()
  options = { ...opts }
  configureApi(opts.apiBase)
  setToken(opts.token)
  const key = localDbKey(me.person.id, opts.scope)
  if (opts.sharedDevice) await keepOnlyThisPerson(key)
  const local = await LocalDb.open(key)
  db = local
  state.me = me
  state.scope = opts.scope ?? null
  cursor = (await local.getMeta<number>("cursor")) ?? 0
  accessVersion = await local.getMeta<number>("accessVersion")
  state.threads = (await local.getMeta<Thread[]>("threads")) ?? []
  state.messages = {}
  addMessages(await local.allMessages())
  state.outbox = await local.allOutbox()
  state.openThreadId = null
  state.orgFilter = null
  state.incidents = (await local.getMeta<Incident[]>("incidents")) ?? []
  state.incidentTeam = false
  state.openIncidentId = null
  state.online = !offline
  state.status = "ready"
  changedThreads.clear()
  fullListAt = 0

  installListeners()
  // A safety net beside realtime and push: nothing depends on a hint arriving.
  pollTimer = setInterval(() => {
    if (document.visibilityState === "visible") void syncNow()
  }, POLL_MS)
  void syncNow()
  void connectRealtime()
}

/** Closes the session; the device copy stays for next time. */
export function stopMessaging() {
  clearTimeout(retryTimer)
  clearTimeout(wsTimer)
  clearInterval(pollTimer)
  const socket = ws
  ws = null
  socket?.close()
  db?.close()
  db = null
  options = null
  syncAgain = false
  state.status = "off"
  state.me = null
  state.scope = null
  state.threads = []
  state.messages = {}
  state.outbox = []
  state.openThreadId = null
  state.incidents = []
  state.incidentTeam = false
  state.openIncidentId = null
  setToken(null)
}

/** Back in the foreground or back online: catch up at once. */
export function wakeMessaging() {
  if (state.status !== "ready") return
  void syncNow()
  if (!ws) {
    wsAttempts = 0
    void connectRealtime()
  }
}

/** Removes a person's device copy, e.g. when their account signs out. */
export async function deleteLocalCopy(personId: string, scope?: string | null) {
  await LocalDb.delete(localDbKey(personId, scope))
}

/** A till is shared: the copies of the people signed in before go. */
async function keepOnlyThisPerson(key: string) {
  let previous: string[] = []
  try {
    previous = JSON.parse(localStorage.getItem(SHARED_DEVICE_KEY) ?? "[]") as string[]
  } catch {
    // Storage blocked or unreadable: nothing to clean up.
  }
  for (const other of previous) {
    if (other !== key) await LocalDb.delete(other)
  }
  try {
    localStorage.setItem(SHARED_DEVICE_KEY, JSON.stringify([key]))
  } catch {
    // Fine: the copy is cleaned up next time storage works.
  }
}

function installListeners() {
  if (listenersInstalled) return
  listenersInstalled = true
  window.addEventListener("online", wakeMessaging)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") wakeMessaging()
  })
}

function addMessages(list: LocalMessage[]) {
  const touched = new Set<string>()
  for (const m of list) {
    const thread = (state.messages[m.threadId] ??= [])
    if (!thread.some((x) => x.id === m.id)) thread.push(m)
    touched.add(m.threadId)
  }
  for (const id of touched) state.messages[id]!.sort((a, b) => a.seq - b.seq)
}

// ---- sync ---------------------------------------------------------------

/** Sends what's in the outbox, then fetches everything after the cursor. Safe to call any time; calls overlap into one run. */
export function syncNow(): Promise<void> {
  if (syncing) {
    syncAgain = true
    return syncing
  }
  syncing = (async () => {
    do {
      syncAgain = false
      await syncOnce()
    } while (syncAgain)
  })().finally(() => {
    syncing = null
  })
  return syncing
}

async function syncOnce() {
  const local = db
  if (!local) return
  try {
    await flushOutbox(local)
    const touched = new Set<string>()
    for (;;) {
      const { data } = await api<SyncResult>(`/api/sync?after=${cursor}&limit=200`)
      if (db !== local) return // another session meanwhile
      if (accessVersion !== undefined && data.accessVersion !== accessVersion) {
        // Many threads: fill in the newly visible ones and drop the ones gone, then go on from the cursor
        if (state.threads.length > FULL_LIST_MAX && (await fillNewAccess(local, data.accessVersion))) continue
        if (db !== local) return
        // New access: older messages in newly visible threads sit below the cursor. Start over.
        fullListAt = 0
        accessVersion = data.accessVersion
        await local.setMeta("accessVersion", accessVersion)
        await local.clearMessages()
        cursor = 0
        state.messages = {}
        continue
      }
      if (accessVersion === undefined) {
        accessVersion = data.accessVersion
        await local.setMeta("accessVersion", accessVersion)
      }
      await local.applySyncPage(data.messages, data.cursor)
      cursor = data.cursor
      addMessages(data.messages)
      for (const m of data.messages) touched.add(m.threadId)
      const confirmed = new Set(data.messages.map((m) => m.id))
      state.outbox = state.outbox.filter((e) => !confirmed.has(e.id))
      if (!data.hasMore) break
    }
    // A send the server confirmed should come back through sync by the time
    // the cursor passes its number. If it hasn't, a database failover lost
    // it: send it again (its id makes that safe).
    for (const entry of state.outbox) {
      if (entry.state === "sent" && entry.seq !== undefined && entry.seq <= cursor) {
        entry.state = "pending"
        delete entry.seq
        await local.putOutbox(plain(entry))
        syncAgain = true
      }
    }
    await refreshThreads(local, touched)
    if (db !== local) return
    // Incidents don't hold up the conversations, unless the network is down for both
    await loadIncidents(local).catch((err) => {
      if (isTemporary(err)) throw err
      console.warn("[hub] loading incidents failed", err)
    })
    setOnline(true)
  } catch (err) {
    if (db === local) await handleError(err)
  }
}

/**
 * The thread list after a sync: the whole list for most people, as it always was. For someone with
 * many threads, only the ones that changed (the sync's messages, realtime hints), and the whole list
 * at the start and every few minutes, which also catches anything a lost hint left out.
 */
async function refreshThreads(local: LocalDb, touched: Set<string>) {
  for (const id of changedThreads) touched.add(id)
  changedThreads.clear()
  const partial = state.threads.length > FULL_LIST_MAX && fullListAt > 0 && Date.now() - fullListAt < FULL_LIST_MS
  if (partial && !touched.size) return
  try {
    if (partial && touched.size <= CHANGED_MAX) {
      const ids = [...touched]
      const { data } = await api<{ threads: Thread[]; only?: boolean }>(`/api/threads?ids=${ids.join(",")}`)
      if (db !== local) return
      if (data.only) {
        const byId = new Map(state.threads.map((th) => [th.id, th]))
        // One not in the answer isn't the person's to see any more
        for (const id of ids) byId.delete(id)
        for (const th of data.threads) byId.set(th.id, th)
        state.threads = [...byId.values()].sort((a, b) => b.lastSeq - a.lastSeq)
      } else {
        // An older Hub answers with every thread
        state.threads = data.threads
        fullListAt = Date.now()
      }
    } else {
      const { data } = await api<{ threads: Thread[] }>("/api/threads")
      if (db !== local) return
      state.threads = data.threads
      fullListAt = Date.now()
    }
  } catch (err) {
    // Fetched next time
    for (const id of touched) changedThreads.add(id)
    throw err
  }
  await local.setMeta("threads", JSON.parse(JSON.stringify(state.threads)))
}

/**
 * Access changed for someone with many threads (a shop more or less): instead of syncing everything
 * again, fetch the messages of the threads now visible up to the cursor and drop the threads gone.
 * False when it can't (an older Hub): then the sync starts over as before.
 */
async function fillNewAccess(local: LocalDb, newVersion: number): Promise<boolean> {
  const { data: list } = await api<{ threads: Thread[] }>("/api/threads")
  if (db !== local) return true
  const before = new Set(state.threads.map((th) => th.id))
  const now = new Set(list.threads.map((th) => th.id))
  const added = list.threads.filter((th) => !before.has(th.id) && th.lastSeq > 0).map((th) => th.id)
  const gone = [...before].filter((id) => !now.has(id))
  for (let i = 0; i < added.length; i += 200) {
    const threadIds = added.slice(i, i + 200)
    let after = 0
    for (;;) {
      let page: SyncResult
      try {
        page = (await api<SyncResult>("/api/sync/threads", { body: { threadIds, after, until: cursor, limit: 500 } })).data
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return false
        throw err
      }
      if (db !== local) return true
      await local.putMessages(page.messages)
      addMessages(page.messages)
      after = page.cursor
      if (!page.hasMore) break
    }
  }
  if (gone.length) {
    await local.deleteThreads(gone)
    for (const id of gone) delete state.messages[id]
  }
  state.threads = list.threads
  fullListAt = Date.now()
  await local.setMeta("threads", JSON.parse(JSON.stringify(list.threads)))
  // Last: a stop halfway does it all again next time
  accessVersion = newVersion
  await local.setMeta("accessVersion", newVersion)
  return true
}

async function flushOutbox(local: LocalDb) {
  const pending = state.outbox.filter((e) => e.state === "pending").sort((a, b) => a.createdAt - b.createdAt)
  for (const entry of pending) {
    try {
      const { data } = await api<{ message: LocalMessage }>(`/api/threads/${entry.threadId}/messages`, {
        body: {
          id: entry.id,
          body: entry.body,
          asOrganisationId: entry.asOrganisationId,
          attachments: entry.attachments?.length ? entry.attachments.map((a) => a.id) : undefined,
        },
      })
      entry.state = "sent"
      entry.seq = data.message.seq
      await local.putOutbox(plain(entry))
    } catch (err) {
      if (isTemporary(err) || (err instanceof ApiError && err.status === 401)) throw err
      entry.state = "failed"
      entry.error = err instanceof Error ? err.message : String(err)
      await local.putOutbox(plain(entry))
    }
  }
}

function setOnline(online: boolean) {
  state.online = online
  if (online) {
    retryDelay = 0
    clearTimeout(retryTimer)
  }
}

/** A refused token: renewed once (at most every 30 s), else the session ends. */
async function renewToken(): Promise<boolean> {
  const renew = options?.renewToken
  if (!renew || Date.now() - lastRenewal < 30_000) return false
  renewing ??= (async () => {
    lastRenewal = Date.now()
    const token = await renew().catch(() => null)
    if (!token || !options) return false
    options.token = token
    setToken(token)
    return true
  })().finally(() => {
    renewing = null
  })
  return renewing
}

function end(reason: "expired" | "no_access") {
  const ended = options?.onEnded
  stopMessaging()
  ended?.(reason)
}

async function handleError(err: unknown) {
  if (err instanceof ApiError && err.status === 401) {
    if (await renewToken()) {
      syncAgain = true
      return
    }
    end("expired")
    return
  }
  if (err instanceof ApiError && err.status === 403 && err.code === "no_access") {
    end("no_access")
    return
  }
  if (isTemporary(err)) {
    setOnline(false)
    retryDelay = Math.min(60_000, retryDelay ? retryDelay * 2 : 2_000)
    clearTimeout(retryTimer)
    retryTimer = setTimeout(() => void syncNow(), retryDelay)
    return
  }
  console.error("[hub] sync failed", err)
}

// ---- realtime -----------------------------------------------------------

async function connectRealtime() {
  if (state.status !== "ready" || ws) return
  const forSession = db
  clearTimeout(wsTimer)
  try {
    const { data } = await api<{ ticket: string }>("/api/ws-ticket", { method: "POST" })
    if (db !== forSession || ws) return
    const socket = new WebSocket(wsUrl(`/ws?ticket=${encodeURIComponent(data.ticket)}`))
    ws = socket
    socket.onopen = () => {
      wsAttempts = 0
      void syncNow()
    }
    socket.onmessage = (event) => {
      try {
        const hint = JSON.parse(String(event.data)) as { type?: string; id?: unknown }
        if (hint.type === "sync") void syncNow()
        else if (hint.type === "thread" && typeof hint.id === "string") {
          changedThreads.add(hint.id)
          void syncNow()
        } else if (hint.type === "incidents" && db) void loadIncidents(db).catch(() => {})
      } catch {
        // Ignore anything we don't understand.
      }
    }
    socket.onclose = (event) => {
      if (ws !== socket) return // closed on purpose: another session or sign-out
      ws = null
      if (state.status !== "ready") return
      // 4003: access changed. Sync shows the current picture before reconnecting.
      if (event.code === 4003) void syncNow()
      scheduleRealtime()
    }
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      await handleError(err)
      if (state.status === "ready") scheduleRealtime()
      return
    }
    scheduleRealtime()
  }
}

function scheduleRealtime() {
  clearTimeout(wsTimer)
  const delay = Math.min(30_000, 1_000 * 2 ** wsAttempts++)
  wsTimer = setTimeout(() => void connectRealtime(), delay)
}

// ---- incidents -------------------------------------------------------------

/** The incidents the person sees, kept on the device too: they matter most when the network doesn't work. */
async function loadIncidents(local: LocalDb) {
  // Customers have none to see
  if (!state.me?.organisations.length) return
  const { data } = await api<{ incidents: Incident[]; team: boolean }>("/api/incidents")
  if (db !== local) return
  state.incidents = data.incidents
  state.incidentTeam = data.team
  await local.setMeta("incidents", JSON.parse(JSON.stringify(data.incidents)))
}

export async function refreshIncidents() {
  if (db) await loadIncidents(db)
}

/** Shown at the top of the screens: going on now, or resolved in the last hours. */
export function currentIncidents(now = Date.now()): Incident[] {
  return state.incidents.filter((i) => !i.resolvedAt || now - new Date(i.resolvedAt).getTime() < 3 * 3600 * 1000)
}

/** The Johku team: every shop in Hub, to choose the ones an incident is for. */
export async function listIncidentShops(): Promise<{ id: string; name: string }[]> {
  return (await api<{ shops: { id: string; name: string }[] }>("/api/incidents/shops")).data.shops
}

/** The Johku team: a new incident, sent to the staff of every shop (shops: null) or of the chosen ones. */
export async function postIncident(input: {
  title: string
  severity: Severity
  status: Status
  body: string
  shops: string[] | null
  email: boolean
}): Promise<{ incidentId: string; recipients: number }> {
  const { data } = await api<{ incidentId: string; recipients: number }>("/api/incidents", { body: { id: uuid(), ...input } })
  await refreshIncidents()
  return data
}

/** The Johku team: the incident's status now and what to tell, to the same shops' staff. */
export async function postIncidentUpdate(incidentId: string, input: { status: Status; body: string; email: boolean }): Promise<{ recipients: number }> {
  const { data } = await api<{ recipients: number }>(`/api/incidents/${incidentId}/updates`, { body: { id: uuid(), ...input } })
  await refreshIncidents()
  return data
}

// ---- order conversations ---------------------------------------------------

/** A shop's thread for an order, if anyone has written yet. The POS stored the order's details first. */
export async function lookupOrderThread(organisationId: string, orderId: string): Promise<string | null> {
  const { data } = await api<{ threadId: string | null }>(
    `/api/order-threads?organisationId=${encodeURIComponent(organisationId)}&orderId=${encodeURIComponent(orderId)}`,
  )
  return data.threadId
}

/** Starts an order's conversation: the thread, then the first message through the outbox. */
export async function startOrderThread(organisationId: string, orderId: string, body: string): Promise<string> {
  const { data } = await api<{ id: string }>("/api/order-threads", { body: { organisationId, orderId } })
  await sendMessage(data.id, body)
  return data.id
}

// ---- actions ------------------------------------------------------------

export async function sendMessage(threadId: string, body: string, asOrganisationId?: string, attachments: Attachment[] = []) {
  if (!db || (!body.trim() && !attachments.length)) return
  const entry: OutboxEntry = {
    id: uuid(),
    threadId,
    body: body.trim(),
    asOrganisationId,
    attachments: attachments.length ? attachments.map((a) => ({ ...a })) : undefined,
    createdAt: Date.now(),
    state: "pending",
  }
  // Stored on the device before the first attempt: it survives a dropped network or a closed app.
  await db.putOutbox(entry)
  state.outbox.push(entry)
  void syncNow()
}

/** A message matching a search. The snippet marks the matching words between \uE000 and \uE001. */
export interface SearchHit {
  messageId: string
  threadId: string
  createdAt: string
  snippet: string
}

const MATCH_START = "\uE000"
const MATCH_END = "\uE001"

/** Messages matching the words, newest first: from Hub when it can be reached, else the device's copy. */
export async function searchMessages(text: string): Promise<SearchHit[]> {
  const known = new Set(state.threads.map((th) => th.id))
  if (state.online) {
    try {
      const { data } = await api<{ hits: SearchHit[] }>(`/api/search?q=${encodeURIComponent(text)}`)
      return data.hits.filter((h) => known.has(h.threadId))
    } catch (err) {
      if (!isTemporary(err)) throw err
    }
  }
  return searchDevice(text)
}

/** As Hub searches: every word, matched by its beginning. */
function searchDevice(text: string): SearchHit[] {
  const words = (text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []).slice(0, 8)
  if (!words.length) return []
  const starts = words.map((w) => new RegExp(`(?<![\\p{L}\\p{N}])${w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}[\\p{L}\\p{N}]*`, "giu"))
  const hits: SearchHit[] = []
  for (const list of Object.values(state.messages)) {
    for (const m of list) {
      if (!starts.every((re) => (re.lastIndex = 0, re.test(m.body)))) continue
      starts[0]!.lastIndex = 0
      const first = starts[0]!.exec(m.body)?.index ?? 0
      const from = Math.max(0, first - 40)
      let snippet = (from > 0 ? "…" : "") + m.body.slice(from, first + 100) + (first + 100 < m.body.length ? "…" : "")
      for (const re of starts) snippet = snippet.replace(re, (w) => `${MATCH_START}${w}${MATCH_END}`)
      hits.push({ messageId: m.id, threadId: m.threadId, createdAt: m.createdAt, snippet })
    }
  }
  return hits.sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 40)
}

/** A snippet's text, split into plain and matching parts. */
export function snippetParts(snippet: string): { text: string; match: boolean }[] {
  return snippet
    .split(MATCH_START)
    .flatMap((part, i) => {
      if (i === 0) return [{ text: part, match: false }]
      const [match = "", rest = ""] = part.split(MATCH_END)
      return [{ text: match, match: true }, { text: rest, match: false }]
    })
    .filter((p) => p.text)
}

/** Uploads a file to a thread for the next message; the message takes it when it's sent. */
export async function uploadAttachment(threadId: string, file: Blob, name: string, onProgress?: (fraction: number) => void): Promise<Attachment> {
  const { attachment } = await uploadFile<{ attachment: Attachment }>(`/api/threads/${threadId}/attachments`, file, name, onProgress)
  // Shown from the device until the message is sent and the server has it
  if (file.type.startsWith("image/")) localPreviews.set(attachment.id, URL.createObjectURL(file))
  return attachment
}

const localPreviews = new Map<string, string>()
const links = new Map<string, { url: string; until: number }>()

/** A file's address for a while: the picture just uploaded from this device, else a signed link. */
export async function attachmentUrl(id: string): Promise<string> {
  const local = localPreviews.get(id)
  if (local) return local
  const known = links.get(id)
  if (known && known.until > Date.now() + 60_000) return known.url
  const { data } = await api<{ path: string; expiresAt: string }>(`/api/attachments/${id}/link`)
  const url = apiUrl(data.path)
  links.set(id, { url, until: new Date(data.expiresAt).getTime() })
  return url
}

export async function discardFailed(id: string) {
  await db?.deleteOutbox(id)
  state.outbox = state.outbox.filter((e) => e.id !== id)
}

export async function markRead(threadId: string) {
  const thread = state.threads.find((th) => th.id === threadId)
  const last = state.messages[threadId]?.at(-1)
  if (!thread || !last || last.seq <= thread.lastReadSeq) return
  thread.lastReadSeq = last.seq
  await api(`/api/threads/${threadId}/read`, { body: { seq: last.seq } }).catch(() => {})
}

export async function createThread(
  input:
    | { kind: "organisations"; asOrganisationId: string; withOrganisationId: string; subject: string }
    | { kind: "private"; withEmail: string; subject: string },
): Promise<string> {
  const { data } = await api<{ id: string }>("/api/threads", { body: input })
  changedThreads.add(data.id)
  await syncNow()
  return data.id
}

/** The invited shop accepts or declines another merchant's conversation. */
export async function answerInvitation(threadId: string, accept: boolean) {
  await api(`/api/threads/${threadId}/invitation`, { body: { accept } })
  changedThreads.add(threadId)
  await syncNow()
}

/** A sales channel offer to the other shop in the conversation. */
export async function makeOffer(threadId: string, input: { asOrganisationId: string; commission: number; note: string }) {
  await api(`/api/threads/${threadId}/proposals`, { body: { id: uuid(), ...input } })
  await syncNow()
}

export async function getProposal(messageId: string): Promise<Proposal> {
  return (await api<{ proposal: Proposal }>(`/api/proposals/${messageId}`)).data.proposal
}

export async function decideProposal(messageId: string, decision: "accept" | "decline" | "withdraw"): Promise<Proposal> {
  const { data } = await api<{ proposal: Proposal }>(`/api/proposals/${messageId}/decision`, { body: { decision } })
  await syncNow()
  return data.proposal
}

/** Whether other Johku merchants find the shop, and what they read about it (its administrators). */
export async function setListing(organisationId: string, listed: boolean, about: string) {
  await api("/api/listing", { body: { organisationId, listed, about } })
  const org = state.me?.organisations.find((o) => o.id === organisationId)
  if (org) Object.assign(org, { listed, about })
}

/** Notifications of an organisation's new messages on or off, for this person. Incidents come either way. */
export async function setNotifyMessages(organisationId: string, on: boolean) {
  await api("/api/me/notifications", { body: { organisationId, messages: on } })
  const org = state.me?.organisations.find((o) => o.id === organisationId)
  if (org) org.notifyMessages = on
}

/** More organisations than this, and lists of them get a search instead of a row of chips */
export const MANY_ORGANISATIONS = 8

/** A shop's collections that have members: who a group message can go to. */
export async function listCollections(organisationId: string): Promise<{ id: string; name: string; members: number }[]> {
  const { data } = await api<{ collections: { id: string; name: string; members: number }[] }>(
    `/api/collections?organisationId=${encodeURIComponent(organisationId)}`,
  )
  return data.collections
}

/** A group message to a collection's members. Each member gets it in a conversation of their own. */
export async function sendGroupMessage(input: { organisationId: string; collectionId: string; subject: string; body: string }): Promise<string> {
  const { data } = await api<{ threadId: string }>("/api/broadcasts", { body: { id: uuid(), ...input } })
  changedThreads.add(data.threadId)
  await syncNow()
  return data.threadId
}

/** Who you can write to as one of your organisations */
export async function listContactable(asOrganisationId: string): Promise<{ id: string; name: string; kind: string }[]> {
  const { data } = await api<{ organisations: { id: string; name: string; kind: string }[] }>(
    `/api/organisations?as=${encodeURIComponent(asOrganisationId)}`,
  )
  return data.organisations
}

// ---- views --------------------------------------------------------------

export function threadMessages(threadId: string): ShownMessage[] {
  const confirmed: ShownMessage[] = (state.messages[threadId] ?? []).map((m) => ({ ...m, status: "delivered" }))
  const known = new Set(confirmed.map((m) => m.id))
  const me = state.me
  const queued: ShownMessage[] = state.outbox
    .filter((e) => e.threadId === threadId && !known.has(e.id))
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((e) => ({
      id: e.id,
      seq: null,
      threadId,
      authorPersonId: me?.person.id ?? "",
      authorName: me?.person.name || me?.person.email,
      authorOrganisationId: e.asOrganisationId ?? null,
      body: e.body,
      attachments: e.attachments ?? null,
      createdAt: new Date(e.createdAt).toISOString(),
      status: e.state === "failed" ? "failed" : "sending",
      error: e.error,
    }))
  return [...confirmed, ...queued]
}

export function threadTitle(thread: Thread): string {
  if (thread.subject) return thread.subject
  if (thread.kind === "member" && thread.memberContext) {
    // A member sees the shop; the shop sees the member
    return thread.asCustomer ? (thread.organisations[0]?.name ?? t("Conversation")) : thread.memberContext.name || thread.memberContext.email
  }
  if (thread.kind === "order" && thread.orderContext && thread.asCustomer) {
    // Your own order: the shop you're talking to
    const shop = thread.organisations[0]?.name
    return shop ? `${t("Order {id}", { id: thread.orderContext.orderId })} · ${shop}` : t("Order {id}", { id: thread.orderContext.orderId })
  }
  if (thread.kind === "order" && thread.orderContext) {
    const { orderId, customerName } = thread.orderContext
    return customerName ? `${t("Order {id}", { id: orderId })} · ${customerName}` : t("Order {id}", { id: orderId })
  }
  const me = state.me
  const myOrgs = new Set(me?.organisations.map((o) => o.id))
  const others = [
    ...thread.organisations.filter((o) => !myOrgs.has(o.id)).map((o) => o.name),
    ...thread.people.filter((p) => p.id !== me?.person.id).map((p) => p.name || p.email),
  ]
  return others.join(", ") || t("Conversation")
}

/** A message is yours: you wrote it, or you're the order's customer and the customer wrote it. */
export function isMine(thread: Thread | undefined, message: { authorPersonId: string | null; authorCustomer?: boolean }): boolean {
  if (message.authorPersonId) return message.authorPersonId === state.me?.person.id
  return !!thread?.asCustomer && !!message.authorCustomer
}

export function isUnread(thread: Thread): boolean {
  if (thread.lastSeq <= thread.lastReadSeq) return false
  if (thread.asCustomer && thread.lastAuthorCustomer) return false
  return thread.lastAuthorPersonId !== state.me?.person.id
}

/** Unread messages in a conversation, from this device's copy (at least 1 when the server says it's unread). */
export function unreadCount(thread: Thread): number {
  if (!isUnread(thread)) return 0
  const n = (state.messages[thread.id] ?? []).filter((m) => m.seq > thread.lastReadSeq && !isMine(thread, m)).length
  return Math.max(1, n)
}

/** Unread conversations in the open session. */
export function unreadThreadCount(): number {
  return state.threads.filter((th) => isUnread(th)).length
}

/** The orders whose conversation has unread messages (order ids), e.g. for the POS's order tab. */
export function unreadOrderIds(): string[] {
  return state.threads.filter((th) => th.kind === "order" && th.orderContext && isUnread(th)).map((th) => th.orderContext!.orderId)
}

/** Customers waiting for a reply in a shop: its order conversations with unread messages. */
export function waitingCustomers(organisationId: string): number {
  return state.threads.filter(
    (th) => th.kind === "order" && th.organisations.some((o) => o.id === organisationId) && isUnread(th),
  ).length
}

/** Unread conversations of one organisation. */
export function organisationUnread(organisationId: string): number {
  return state.threads.filter((th) => th.organisations.some((o) => o.id === organisationId) && isUnread(th)).length
}

/** The organisation a conversation is mostly about, for its avatar: the other side, else mine. */
export function threadOrganisation(thread: Thread): { id: string; name: string; logo?: string } | null {
  const mine = new Set(state.me?.organisations.map((o) => o.id))
  return thread.organisations.find((o) => !mine.has(o.id)) ?? thread.organisations[0] ?? null
}

/**
 * Who a conversation's avatar shows: an order's picture (the shop's side) or its customer, the
 * other organisation with its logo, or the other person. image: a picture's address at `size`,
 * when there is one; photo: it's a product's picture, not a logo.
 */
export function threadAvatar(thread: Thread, size = 44): { name: string; id: string; image: string | null; photo: boolean } {
  if (thread.kind === "order" && thread.orderContext && !thread.asCustomer) {
    const name = thread.orderContext.customerName || threadTitle(thread)
    const picture = threadPicture(thread, size)
    return { name, id: `customer:${name}`, image: picture, photo: !!picture }
  }
  if (thread.kind === "private") {
    const other = thread.people.find((p) => p.id !== state.me?.person.id)
    if (other) return { name: other.name || other.email, id: other.id, image: null, photo: false }
  }
  const org = threadOrganisation(thread)
  return org
    ? { name: org.name, id: org.id, image: organisationLogo(org, size), photo: false }
    : { name: threadTitle(thread), id: thread.id, image: null, photo: false }
}

/** An order conversation's picture: its first product with one, at width × height. */
export function threadPicture(thread: Thread, width: number, height = width): string | null {
  if (thread.kind !== "order" || !thread.orderContext) return null
  const org = thread.organisations.find((o) => o.id.startsWith("shop:"))
  const item = thread.orderContext.items.find((i) => i.image)
  return item ? shopPicture(org?.id, item.image, width, height) : null
}

/** The latest message in a conversation, confirmed or still in the outbox. */
export function lastMessage(thread: Thread): ShownMessage | undefined {
  return threadMessages(thread.id).at(-1)
}

/** The conversations shown with the current organisation filter. Private ones show only under "All". */
export function visibleThreads(): Thread[] {
  const filter = state.orgFilter
  if (!filter) return state.threads
  return state.threads.filter((th) => th.organisations.some((o) => o.id === filter))
}

/** My organisations taking part in a thread: more than one means the composer asks which to speak for. */
export function mySidesIn(thread: Thread): Organisation[] {
  const ids = new Set(thread.organisations.map((o) => o.id))
  return state.me?.organisations.filter((o) => ids.has(o.id)) ?? []
}
